import axios from 'axios';
import React, { Suspense, useEffect, useState, useRef } from 'react';
import { IoMdAdd, IoMdSend, IoMdExpand, IoMdContract } from "react-icons/io";
import { MdFileUpload, MdLocationOn } from "react-icons/md";
import { FiSend } from 'react-icons/fi';
import SubNav from '../components/subNav';
import SEO from '../components/SEO';
import '../styles/location.css';
import { useParams, useNavigate } from 'react-router-dom';
import { baseUrl } from '../config';
import AutoLocationDetection from '../components/AutoLocationDetection';

const MapComponent = React.lazy(() => import('../components/MapComponent'));

const animationStyles = `
  .loc-loading-container {
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    min-height: 100vh;
    background: linear-gradient(to bottom right, #eff6ff, #e0e7ff);
  }
  .loc-loading-spinner {
    width: 80px; height: 80px;
    border: 8px solid #e0e7ff; border-top-color: #6366f1;
    border-radius: 50%; animation: loc-spin 1s linear infinite;
  }
  @keyframes loc-spin { to { transform: rotate(360deg); } }
  .loc-loading-text { margin-top: 24px; font-size: 22px; font-weight: 600; color: #6366f1; }
  .loc-loading-sub  { margin-top: 8px;  font-size: 15px; color: #64748b; }
  
  /* ===== IMPROVED CHAT MESSAGE STYLES ===== */
  
  /* Chat container */
  #locationMessages {
    padding: 16px;
    max-height: 400px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  
  /* Message wrapper for alignment */
  .message-wrapper {
    display: flex;
    margin-bottom: 8px;
  }
  
  .message-wrapper.user {
    justify-content: flex-end;
  }
  
  .message-wrapper.bot {
    justify-content: flex-start;
  }
  
  /* Base message bubble styles */
  .message-bubble {
    max-width: 75%;
    padding: 12px 16px;
    border-radius: 12px;
    word-wrap: break-word;
    line-height: 1.5;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  }
  
  /* User messages - right side, indigo */
  .locationUserMessage {
    background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
    color: white;
    border-bottom-right-radius: 4px;
    font-size: 14px;
    font-weight: 500;
  }
  
  /* AI messages - left side, white/gray */
  .locationAIMessage {
    background: #ffffff;
    color: #1f2937;
    border-bottom-left-radius: 4px;
    font-size: 14px;
    border: 1px solid #e5e7eb;
  }
  
  /* Markdown formatting inside messages */
  .message-content {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  }
  
  .message-content strong,
  .message-content b {
    font-weight: 700;
    color: inherit;
  }
  
  .locationAIMessage .message-content strong {
    color: #4f46e5;
  }
  
  .message-content em,
  .message-content i {
    font-style: italic;
  }
  
  .message-content p {
    margin: 8px 0;
  }
  
  .message-content p:first-child {
    margin-top: 0;
  }
  
  .message-content p:last-child {
    margin-bottom: 0;
  }
  
  .message-content ul,
  .message-content ol {
    margin: 8px 0;
    padding-left: 24px;
  }
  
  .message-content li {
    margin: 4px 0;
  }
  
  .message-content code {
    background: rgba(0,0,0,0.05);
    padding: 2px 6px;
    border-radius: 4px;
    font-family: 'Courier New', monospace;
    font-size: 13px;
  }
  
  .message-content pre {
    background: rgba(0,0,0,0.05);
    padding: 12px;
    border-radius: 6px;
    overflow-x: auto;
    margin: 8px 0;
  }
  
  /* Location detail card styles */
  .location-detail-card {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    padding: 16px;
    border-radius: 12px;
    margin: 8px 0;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  }
  
  .location-detail-section {
    background: rgba(255,255,255,0.95);
    color: #1f2937;
    padding: 12px;
    border-radius: 8px;
    margin: 8px 0;
  }
  
  .location-detail-section h4 {
    font-size: 14px;
    font-weight: 600;
    margin: 0 0 8px 0;
    color: #4f46e5;
    border-bottom: 2px solid #e5e7eb;
    padding-bottom: 4px;
  }
  
  .location-detail-row {
    display: flex;
    justify-content: space-between;
    margin: 6px 0;
    font-size: 13px;
  }
  
  .location-detail-label {
    font-weight: 500;
    color: #6b7280;
  }
  
  .location-detail-value {
    font-weight: 600;
    color: #1f2937;
  }
  
  .budget-status-good {
    background: #d1fae5;
    color: #059669;
    padding: 8px 12px;
    border-radius: 6px;
    font-weight: 600;
    border: 1px solid #059669;
  }
  
  .budget-status-bad {
    background: #fee2e2;
    color: #dc2626;
    padding: 8px 12px;
    border-radius: 6px;
    font-weight: 600;
    border: 1px solid #dc2626;
  }
  
  .facility-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    margin-top: 8px;
  }
  
  .facility-item {
    background: #f3f4f6;
    padding: 6px 10px;
    border-radius: 6px;
    font-size: 12px;
    text-align: center;
  }
  
  .save-location-btn-inline {
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
    color: white;
    padding: 10px 20px;
    border-radius: 8px;
    border: none;
    font-weight: 600;
    cursor: pointer;
    width: 100%;
    margin-top: 12px;
    transition: all 0.2s;
  }
  
  .save-location-btn-inline:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(5,150,105,0.3);
  }
  
  .save-location-btn-inline:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    transform: none;
  }
  
  /* Emoji and icons in messages */
  .message-content .emoji {
    font-size: 1.2em;
    vertical-align: middle;
  }
`;

// ✨ Simple Markdown Parser
const parseMarkdown = (text) => {
    if (!text || typeof text !== 'string') return text;

    let html = text;

    // Bold: **text** or __text__
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');

    // Italic: *text* or _text_
    html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
    html = html.replace(/_(.+?)_/g, '<em>$1</em>');

    // Code: `text`
    html = html.replace(/`(.+?)`/g, '<code>$1</code>');

    // Links: [text](url)
    html = html.replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

    // Line breaks
    html = html.replace(/\n/g, '<br/>');

    return html;
};

const LocationList = ({ onLocationClick, aiLocations = [] }) => (
    <div id="suggestedLoc">
        <h3>Suggested Locations</h3>
        <ul id="locationList">
            {aiLocations.length > 0
                ? aiLocations.map((loc, i) =>
                    loc.coordinates ? (
                        <li
                            key={`ai-${i}`}
                            onClick={() => onLocationClick(loc)}
                            style={{ borderLeft: '3px solid #6366f1', cursor: 'pointer' }}
                        >
                            <MdLocationOn className="location-icon" style={{ color: '#6366f1' }} />
                            {loc.name}, {loc.city}
                            <span style={{ fontSize: 11, color: '#64748b', marginLeft: 4 }}>
                                · {loc.suitability_score}/10
                            </span>
                        </li>
                    ) : null
                )
                : <>
                    <li onClick={() => onLocationClick({ name: "Mumbai", coordinates: { lat: 19.0760, lng: 72.8777 } })}>
                        <MdLocationOn className="location-icon" /> Mumbai, India
                    </li>
                    <li onClick={() => onLocationClick({ name: "Times Square", coordinates: { lat: 40.7580, lng: -73.9855 } })}>
                        <MdLocationOn className="location-icon" /> Times Square
                    </li>
                    <li onClick={() => onLocationClick({ name: "Central Park", coordinates: { lat: 40.7829, lng: -73.9654 } })}>
                        <MdLocationOn className="location-icon" /> Central Park
                    </li>
                    <li onClick={() => onLocationClick({ name: "London", coordinates: { lat: 51.5074, lng: -0.1278 } })}>
                        <MdLocationOn className="location-icon" /> London
                    </li>
                </>
            }
        </ul>
    </div>
);

export const Location = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();

    const [activeUser, setActiveUser] = useState(0);
    const [isCheckingExisting, setIsChecking] = useState(true);

    const [conversationId, setConversationId] = useState(null);
    const [scoutingReady, setScoutingReady] = useState(false);
    const [aiLocations, setAiLocations] = useState([]);

    const [setupMode, setSetupMode] = useState('pipeline_prompt');
    const [manualGenre, setManualGenre] = useState('');
    const [manualScript, setManualScript] = useState('');
    const [isSending, setIsSending] = useState(false);

    const [chatMessages, setChatMessages] = useState([
        {
            sender: 'bot',
            text: '📍 Hi! I can scout filming locations for your project. Would you like me to use the script you already created in the pipeline, or would you prefer to provide details manually?'
        }
    ]);
    const chatEndRef = useRef(null);

    const [userLocString, setUserLocString] = useState('');
    const [userCoords, setUserCoords] = useState({ lat: null, lng: null });

    const [isLoadingDetails, setIsLoadingDetails] = useState(false);
    const [currentLocationBudget, setCurrentLocationBudget] = useState(0);
    const [isSavingLocation, setIsSavingLocation] = useState(false);
    const [lastFetchedLocationDetails, setLastFetchedLocationDetails] = useState(null);

    const [showAddEntry, setShowAddEntry] = useState(false);
    const [mapCenter, setMapCenter] = useState([40.7605, -73.9934]);
    const [userLocationDetected, setUserLocationDetected] = useState(false);
    const [locationDetails, setLocationDetails] = useState(null);
    const [isClient, setIsClient] = useState(false);
    const [locations, setLocations] = useState([]);
    const [isMapMaximized, setIsMapMaximized] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const mapContainerRef = useRef(null);
    const [formData, setFormData] = useState({
        name: '', address: '', owner: '',
        startDate: '', endDate: '', contact: '', form: null,
        latitude: 40.7605, longitude: -73.9934
    });

    useEffect(() => { setIsClient(true); fetchLocations(); checkProjectPermissions(); }, []);
    useEffect(() => { if (activeUser && projectId) checkExistingScout(); }, [activeUser, projectId]);
    useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

    async function checkProjectPermissions() {
        try {
            // getUserById instead of getUserIDByToken - same single call,
            // but its response also includes user_type at no extra round
            // trip, so the free-tier check below can be skipped entirely
            // for non-students instead of always paying for a
            // /free-tier/status call to learn that.
            const tokenRes = await fetch(`${baseUrl}/getUserById`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
            });
            if (!tokenRes.ok) { navigate('/login'); return; }
            const { user_id, user_type } = await tokenRes.json();
            setActiveUser(user_id);

            const membersRes = await fetch(`${baseUrl}/getMembersByProjectId`, { headers: { project_id: projectId } });
            if (!membersRes.ok) { window.alert('Failed to check project permissions'); navigate('/'); return; }
            const members = await membersRes.json();
            if (!members.map(m => m.user_id).includes(user_id)) {
                window.alert('This user does not have access to this project!'); navigate('/'); return;
            }

            // Free-tier bypass: a currently-eligible verified student skips
            // the subscription check below entirely - checked independently
            // via GET /free-tier/status (same pattern StudentToolSelector.jsx
            // already uses) rather than trusted from navigation state. Only
            // called for user_type === 'student' - a non-student never pays
            // for this extra round trip at all. Anyone else (non-student, or
            // a student who isn't currently eligible - expired, etc.) falls
            // through to the exact existing check below, unchanged.
            if (user_type === 'student') {
                const freeTierRes = await fetch(`${baseUrl}/free-tier/status`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
                });
                if (freeTierRes.ok) {
                    const freeTierStatus = await freeTierRes.json();
                    if (freeTierStatus.state === 'active' || freeTierStatus.state === 'not_started') {
                        return;
                    }
                }
            }

            const toolsRes = await fetch(`${baseUrl}/getAvailableTools`, {
                headers: { project_id: projectId, Authorization: `Bearer ${localStorage.getItem('access_token')}` }
            });
            if (!toolsRes.ok) { window.alert('Failed to check tool permissions'); navigate('/services'); return; }
            const tools = await toolsRes.json();
            if (!tools.map(t => t.name).includes('Location Scouting')) {
                window.alert("You haven't purchased this tool yet!"); navigate('/services');
            }
        } catch (err) { console.error('Permission check error'); window.alert('Error checking permissions'); navigate('/'); }
    }

    async function checkExistingScout() {
        setIsChecking(true);
        try {
            const res = await fetch(
                `${baseUrl}/locationConversation/getConversationHistory?project_id=${projectId}`,
                { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } }
            );
            if (!res.ok) throw new Error();
            const { conversations = [] } = await res.json();
            if (conversations.length > 0) { await loadLocationConversation(conversations[0].id); }
            else { setIsChecking(false); }
        } catch { setIsChecking(false); }
    }

    async function loadLocationConversation(convId) {
        try {
            const res = await fetch(
                `${baseUrl}/locationConversation/getConversation?conversation_id=${convId}`,
                { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } }
            );
            if (!res.ok) throw new Error();
            const data = await res.json();
            const msgs = data.messages
                .filter(m => m.message_type !== 'system')
                .map(m => ({ sender: m.message_type === 'user' ? 'user' : 'bot', text: m.content }));
            if (msgs.length > 0) setChatMessages(msgs);
            const latestAI = [...data.messages].reverse().find(m => m.message_type === 'ai' && m.locations_data?.length);
            if (latestAI?.locations_data) {
                applyAiLocations(latestAI.locations_data);
                if (latestAI.project_context?.location_budget) {
                    setCurrentLocationBudget(latestAI.project_context.location_budget);
                }
            }
            setConversationId(convId); setScoutingReady(true); setSetupMode('done');
        } catch (err) { console.error('Error loading conversation:'); }
        finally { setIsChecking(false); }
    }

    function applyAiLocations(locs) {
        setAiLocations(locs);
        const first = locs.find(l => l.coordinates);
        if (first) setMapCenter([first.coordinates.lat, first.coordinates.lng]);
    }

    async function handlePipelineChoice() {
        addUserMsg('✅ Yes, use the pipeline script');
        addBotMsg('Fetching your script and scouting locations...');
        setIsSending(true);
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 120000);
        try {
            const params = new URLSearchParams({ project_id: projectId });
            if (userLocString) params.set('user_location', userLocString);
            if (userCoords.lat) params.set('user_lat', userCoords.lat);
            if (userCoords.lng) params.set('user_lng', userCoords.lng);

            const res = await fetch(
                `${baseUrl}/locationConversation/generateFromPipeline?${params.toString()}`,
                { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }, signal: controller.signal }
            );
            if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Pipeline scouting failed'); }
            onScoutingComplete(await res.json());
        } catch (err) {
            replaceBotMsg(`❌ ${err.message}. You can provide details manually instead.`);
            setSetupMode('manual_input');
        } finally { clearTimeout(timeoutId); setIsSending(false); }
    }

    function handleManualChoice() {
        addUserMsg("🔧 I'll provide details manually");
        addBotMsg("Sure! Enter the genre(s) of your film and optionally paste a script excerpt. I'll scout appropriate locations based on what you provide.");
        setSetupMode('manual_input');
    }

    async function handleManualSubmit() {
        if (!manualGenre.trim() && !manualScript.trim()) return;
        addUserMsg(`🎬 Genre: ${manualGenre || 'Not specified'}` + (manualScript ? '\n📄 Script excerpt provided' : ''));
        addBotMsg('Scouting locations for your project...');
        setIsSending(true);
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 120000);
        try {
            const fd = new FormData();
            fd.append('project_id', projectId);
            if (manualGenre.trim()) fd.append('genre', manualGenre.trim());
            if (manualScript.trim()) fd.append('script_text', manualScript.trim());
            if (userLocString) fd.append('user_location', userLocString);
            if (userCoords.lat) fd.append('user_lat', userCoords.lat);
            if (userCoords.lng) fd.append('user_lng', userCoords.lng);
            if (conversationId) fd.append('conversation_id', conversationId);

            const res = await fetch(`${baseUrl}/locationConversation/generateFromManual`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
                body: fd,
                signal: controller.signal
            });
            if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Manual scouting failed'); }
            onScoutingComplete(await res.json());
        } catch (err) {
            replaceBotMsg(`❌ ${err.message}. Please try again.`);
        } finally { clearTimeout(timeoutId); setManualGenre(''); setManualScript(''); setIsSending(false); }
    }

    function onScoutingComplete(result) {
        setConversationId(result.conversation_id);
        applyAiLocations(result.locations || []);

        if (result.project_context?.location_budget) {
            setCurrentLocationBudget(result.project_context.location_budget);
        }

        setScoutingReady(true); setSetupMode('done');
        setChatMessages(prev => {
            const updated = [...prev];
            for (let i = updated.length - 1; i >= 0; i--) {
                if (updated[i].sender === 'bot') { updated[i] = { sender: 'bot', text: result.message_content }; break; }
            }
            return updated;
        });
    }

    function renderSetupChat() {
        return (
            <div className="bg-white rounded-xl shadow-lg p-6 flex flex-col h-[500px]">
                <h2 className="text-xl font-semibold text-indigo-700 mb-4">Location Scouting Setup</h2>
                <div className="flex-1 bg-gray-50 rounded-lg p-4 mb-4 overflow-y-auto space-y-3">
                    {chatMessages.map((msg, i) => (
                        <div key={i} className={`message-wrapper ${msg.sender}`}>
                            <div className={`message-bubble ${msg.sender === 'user' ? 'locationUserMessage' : 'locationAIMessage'}`}>
                                <div
                                    className="message-content"
                                    dangerouslySetInnerHTML={{ __html: parseMarkdown(msg.text) }}
                                />
                            </div>
                        </div>
                    ))}
                    <div ref={chatEndRef} />
                </div>
                {setupMode === 'pipeline_prompt' && !isSending && (
                    <div className="flex gap-3">
                        <button onClick={handlePipelineChoice}
                            className="flex-1 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors">
                            ✅ Yes, use pipeline script
                        </button>
                        <button onClick={handleManualChoice}
                            className="flex-1 py-2 border border-indigo-400 text-indigo-700 rounded-lg text-sm font-medium hover:bg-indigo-50 transition-colors">
                            🔧 Provide details manually
                        </button>
                    </div>
                )}
                {setupMode === 'manual_input' && (
                    <div className="space-y-2">
                        <input type="text"
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
                            placeholder="Genre(s) — e.g. Thriller, Drama"
                            value={manualGenre} onChange={e => setManualGenre(e.target.value)}
                            disabled={isSending} autoFocus />
                        <div className="relative">
                            <textarea
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 h-20 resize-none text-sm"
                                placeholder="Paste script excerpt (optional)..."
                                value={manualScript} onChange={e => setManualScript(e.target.value)} disabled={isSending} />
                            <button onClick={handleManualSubmit}
                                disabled={isSending || (!manualGenre.trim() && !manualScript.trim())}
                                className="absolute bottom-2 right-2 p-1.5 bg-indigo-600 rounded-full text-white hover:bg-indigo-700 disabled:opacity-40">
                                <FiSend size={15} />
                            </button>
                        </div>
                    </div>
                )}
                {isSending && (
                    <div className="flex items-center justify-center py-3 text-indigo-600 text-sm">
                        <div className="loc-loading-spinner" style={{ width: 20, height: 20, borderWidth: 3, marginRight: 8 }} />
                        Scouting locations...
                    </div>
                )}
            </div>
        );
    }

    const handleLocationDetected = (locationData) => {
        if (locationData?.coordinates) {
            setMapCenter(locationData.coordinates); setLocationDetails(locationData.details); setUserLocationDetected(true);
            setFormData(prev => ({ ...prev, address: locationData.details.formattedAddress, latitude: locationData.coordinates[0], longitude: locationData.coordinates[1] }));
            setUserLocString(locationData.details.formattedAddress || '');
            setUserCoords({ lat: locationData.coordinates[0], lng: locationData.coordinates[1] });
        }
    };

    const saveLocationToBackend = async () => {
        if (!locationDetails || !userLocationDetected) return;
        try {
            await axios.post(`${baseUrl}/projects/${projectId}/location`, { latitude: mapCenter[0], longitude: mapCenter[1], address: locationDetails.formattedAddress, city: locationDetails.city, state: locationDetails.state, country: locationDetails.country });
            alert("Location saved successfully!");
        } catch (error) { console.error("Error saving location:"); alert("Failed to save location"); }
    };

    const handleLocationClick = async (location) => {
        const coords = [location.coordinates.lat, location.coordinates.lng];
        setMapCenter(coords);
        setSelectedLocation(coords);

        addUserMsg(`📍 Selected: ${location.name}, ${location.city || ''}`);

        if (!conversationId) {
            addBotMsg('⚠️ Please scout locations first before selecting one.');
            return;
        }

        addBotMsg('🔍 Analyzing location details...');
        setIsLoadingDetails(true);

        try {
            const res = await fetch(`${baseUrl}/locationConversation/selectLocation`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                },
                body: JSON.stringify({
                    conversation_id: conversationId,
                    location_name: location.name,
                    lat: location.coordinates.lat,
                    lng: location.coordinates.lng,
                    current_location_budget: currentLocationBudget
                })
            });

            if (!res.ok) {
                throw new Error('Failed to fetch location details');
            }

            const data = await res.json();
            setLastFetchedLocationDetails(data.location_details);
            replaceBotMsg(formatLocationDetailsMessage(data.location_details));

        } catch (error) {
            replaceBotMsg('❌ Failed to load location details. Please try again.');
        } finally {
            setIsLoadingDetails(false);
        }
    };

    const formatLocationDetailsMessage = (details) => {
        if (!details) return '❌ No details available';

        const rental = details.rental_info || {};
        const owner = details.owner_info || {};
        const permits = details.permits_info || {};
        const facilities = details.nearby_facilities || {};
        const budgetComp = details.budget_comparison || {};

        return {
            isHtml: true,
            content: `
        <div class="location-detail-card">
          <h3 style="margin: 0 0 12px 0; font-size: 18px; font-weight: 700;">
            📍 ${details.location_name}
          </h3>
          <p style="margin: 0; font-size: 12px; opacity: 0.9;">
            ${details.coordinates?.lat.toFixed(4)}, ${details.coordinates?.lng.toFixed(4)}
          </p>
        </div>

        ${rental.estimated_daily_cost ? `
          <div class="location-detail-section">
            <h4>💰 Rental Costs</h4>
            <div class="location-detail-row">
              <span class="location-detail-label">Daily Rate:</span>
              <span class="location-detail-value" style="color: #059669; font-size: 16px;">
                ₹${rental.estimated_daily_cost?.toLocaleString()}/day
              </span>
            </div>
            <div class="location-detail-row">
              <span class="location-detail-label">Weekly Rate:</span>
              <span class="location-detail-value">
                ₹${rental.estimated_weekly_cost?.toLocaleString()}
              </span>
            </div>
            <div style="margin-top: 8px; font-size: 11px; color: #6b7280; background: #f9fafb; padding: 6px; border-radius: 4px;">
              <strong>Source:</strong> ${rental.source || 'Web crawl'} | 
              <strong>Confidence:</strong> ${rental.cost_confidence || 'N/A'}
            </div>
          </div>
        ` : `
          <div class="location-detail-section">
            <h4>💰 Rental Costs</h4>
            <p style="color: #6b7280; font-size: 13px; margin: 0;">
              Cost not publicly available. Contact owner for pricing.
            </p>
          </div>
        `}

        ${budgetComp.allocated > 0 ? `
          <div class="location-detail-section">
            <h4>📊 Budget Analysis</h4>
            <div class="${budgetComp.within_budget ? 'budget-status-good' : 'budget-status-bad'}">
              ${budgetComp.within_budget ? '✅' : '⚠️'} 
              ${budgetComp.within_budget ? 'Within Budget' : 'Over Budget'}
            </div>
            <div class="location-detail-row" style="margin-top: 8px;">
              <span class="location-detail-label">Allocated:</span>
              <span class="location-detail-value">₹${budgetComp.allocated?.toLocaleString()}</span>
            </div>
            <div class="location-detail-row">
              <span class="location-detail-label">Estimated Cost:</span>
              <span class="location-detail-value">₹${budgetComp.estimated_cost?.toLocaleString()}</span>
            </div>
            <div class="location-detail-row">
              <span class="location-detail-label">Difference:</span>
              <span style="color: ${budgetComp.difference >= 0 ? '#dc2626' : '#059669'}; font-weight: 700;">
                ${budgetComp.difference >= 0 ? '+' : ''}₹${budgetComp.difference?.toLocaleString()}
              </span>
            </div>
            ${details.budget_updated ? `
              <div style="margin-top: 10px; background: #fef3c7; padding: 8px; border-radius: 6px; font-size: 12px;">
                🔔 <strong>Budget Auto-Updated!</strong> Check Budget tool for details.
              </div>
            ` : ''}
          </div>
        ` : ''}

        <div class="location-detail-section">
          <h4>📞 Contact Information</h4>
          ${owner.osm_contacts?.length > 0 ? `
            ${owner.osm_contacts.slice(0, 2).map(contact => `
              <div style="background: #f9fafb; padding: 8px; border-radius: 6px; margin: 6px 0; border: 1px solid #e5e7eb;">
                <div style="font-weight: 600; color: #1f2937; font-size: 13px;">${contact.name}</div>
                <div style="font-size: 12px; color: #4b5563; margin-top: 2px;">📱 ${contact.phone}</div>
                ${contact.type ? `<div style="font-size: 11px; color: #6b7280; margin-top: 2px;">Type: ${contact.type}</div>` : ''}
              </div>
            `).join('')}
          ` : `
            <p style="color: #6b7280; font-size: 13px; margin: 0;">
              Contact info not available. Verify with local film commission.
            </p>
          `}
        </div>

        <div class="location-detail-section">
          <h4>📋 Filming Permits</h4>
          ${permits.permit_summary ? `
            <div style="background: #fef3c7; padding: 10px; border-radius: 6px; font-size: 13px; line-height: 1.5;">
              ${permits.permit_summary}
            </div>
          ` : `
            <p style="color: #6b7280; font-size: 13px; margin: 0;">
              Permit info not available. Contact local authorities.
            </p>
          `}
          <p style="font-size: 11px; color: #6b7280; margin: 8px 0 0 0; font-style: italic;">
            💡 ${permits.recommendation || 'Always verify with local municipal authority'}
          </p>
        </div>

        ${Object.keys(facilities).length > 0 ? `
          <div class="location-detail-section">
            <h4>🏪 Nearby Facilities</h4>
            <div class="facility-grid">
              ${facilities.parking > 0 ? `<div class="facility-item">🅿️ Parking: ${facilities.parking}</div>` : ''}
              ${facilities.hotels > 0 ? `<div class="facility-item">🏨 Hotels: ${facilities.hotels}</div>` : ''}
              ${facilities.restaurants > 0 ? `<div class="facility-item">🍽️ Restaurants: ${facilities.restaurants}</div>` : ''}
              ${facilities.hospitals > 0 ? `<div class="facility-item">🏥 Hospitals: ${facilities.hospitals}</div>` : ''}
              ${facilities.cafes > 0 ? `<div class="facility-item">☕ Cafes: ${facilities.cafes}</div>` : ''}
              ${facilities.shops > 0 ? `<div class="facility-item">🛒 Shops: ${facilities.shops}</div>` : ''}
            </div>
          </div>
        ` : ''}

        <button 
          onclick="window.saveCurrentLocation()" 
          class="save-location-btn-inline"
          ${isSavingLocation ? 'disabled' : ''}
        >
          ${isSavingLocation ? '💾 Saving...' : '💾 Save to My Locations'}
        </button>
      `
        };
    };

    const handleSaveFromChat = async () => {
        if (!lastFetchedLocationDetails) {
            alert('No location details to save');
            return;
        }

        setIsSavingLocation(true);

        try {
            const rental = lastFetchedLocationDetails.rental_info || {};
            const owner = lastFetchedLocationDetails.owner_info || {};
            const coords = lastFetchedLocationDetails.coordinates || {};

            const locationData = {
                locationtitle: lastFetchedLocationDetails.location_name,
                addressline: lastFetchedLocationDetails.location_name,
                user: owner.osm_contacts?.[0]?.name || "Unknown",
                startavailability: new Date().toISOString().split('T')[0],
                end_availability: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                contact: owner.osm_contacts?.[0]?.phone || "Contact via film commission",
                latitude: coords.lat || 0,
                longitude: coords.lng || 0,
                notes: rental.estimated_daily_cost && rental.estimated_daily_cost > 0
                    ? `Estimated rental: ₹${rental.estimated_daily_cost.toLocaleString()}/day`
                    : ""
            };
            const response = await axios.post(
                `${baseUrl}/locations?project_id=${projectId}`,
                locationData,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                    }
                }
            );
            addBotMsg('✅ Location saved successfully to your database!');
            fetchLocations(); // ✅ will now reload only this project's locations

        } catch (error) {
            if (error.response) {
                const errorMsg = typeof error.response.data.detail === 'string'
                    ? error.response.data.detail
                    : JSON.stringify(error.response.data.detail);
                addBotMsg(`❌ Failed to save: ${errorMsg}`);
            } else {
                addBotMsg('❌ Failed to save location. Please try again.');
            }
        } finally {
            setIsSavingLocation(false);
        }
    };


    useEffect(() => {
        window.saveCurrentLocation = handleSaveFromChat;
        return () => {
            delete window.saveCurrentLocation;
        };
    }, [lastFetchedLocationDetails, isSavingLocation]);

    const handleAIResponse = async () => {
        const messageText = document.getElementById('budgetAILanding')?.value;
        if (!messageText) return;

        addUserMsg(messageText);
        document.getElementById('budgetAILanding').value = '';

        if (messageText.toLowerCase().includes('location') ||
            messageText.toLowerCase().includes('address') ||
            messageText.toLowerCase().includes('place')) {

            if (messageText.includes("Mumbai")) {
                setMapCenter([19.0760, 72.8777]);
                flyToLocation([19.0760, 72.8777]);
                addBotMsg("I've centered the map on **Mumbai, India**.");
            } else if (messageText.includes("Times Square")) {
                setMapCenter([40.7580, -73.9855]);
                flyToLocation([40.7580, -73.9855]);
                addBotMsg("I've centered the map on **Times Square, New York**.");
            } else if (messageText.includes("Central Park")) {
                setMapCenter([40.7829, -73.9654]);
                flyToLocation([40.7829, -73.9654]);
                addBotMsg("I've centered the map on **Central Park, New York**.");
            } else {
                addBotMsg("How can I help with your location management? You can ask me to find specific locations.");
            }
        } else {
            addBotMsg("How can I help with your location management? You can ask me to find specific locations.");
        }
    };

    const fetchLocations = async () => {
        try {
            const res = await axios.get(`${baseUrl}/locations?project_id=${projectId}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('access_token')}`
                }
            });
            setLocations(res.data);
        } catch (error) {
        }
    };

    const handleInputChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
    const handleFileChange = (e) => setFormData({ ...formData, form: e.target.files[0] });
    const toggleMapSize = () => setIsMapMaximized(!isMapMaximized);
    const toggleAddEntry = () => setShowAddEntry(!showAddEntry);
    const flyToLocation = (coordinates) => { setMapCenter(coordinates); setSelectedLocation(coordinates); };

    const addLocation = async () => {
        const locationData = {
            locationtitle: formData.name,
            addressline: formData.address,
            user: formData.owner,
            startavailability: formData.startDate
                ? new Date(formData.startDate).toISOString().split('T')[0]
                : null,
            end_availability: formData.endDate
                ? new Date(formData.endDate).toISOString().split('T')[0]
                : null,
            contact: formData.contact,
            latitude: String(formData.latitude),
            longitude: String(formData.longitude)
        };

        try {
            await axios.post(
                `${baseUrl}/locations?project_id=${projectId}`,   // ✅ project_id as query param
                locationData,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                    }
                }
            );
            setShowAddEntry(false);
            fetchLocations();
        } catch (error) {
            if (error.response) {
                alert(`Failed to add location: ${error.response.data.detail || 'Validation error'}`);
            } else {
                alert('Failed to add location');
            }
        }
    };


    const deleteLocation = async (locationId) => {
        try {
            await axios.delete(`${baseUrl}/locations/${locationId}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('access_token')}`
                }
            });
            fetchLocations();
        } catch (error) {
        }
    };

    function addUserMsg(text) {
        setChatMessages(prev => [...prev, { sender: 'user', text }]);
    }

    function addBotMsg(content) {
        setChatMessages(prev => [...prev, { sender: 'bot', text: content }]);
    }

    function replaceBotMsg(content) {
        setChatMessages(prev => {
            const u = [...prev];
            for (let i = u.length - 1; i >= 0; i--) {
                if (u[i].sender === 'bot') {
                    u[i] = { sender: 'bot', text: content };
                    break;
                }
            }
            return u;
        });
    }

    if (isCheckingExisting) {
        return (
            <><style>{animationStyles}</style><SubNav />
                <div className="loc-loading-container">
                    <div className="loc-loading-spinner" />
                    <div className="loc-loading-text">Loading your location workspace...</div>
                    <div className="loc-loading-sub">Checking for existing scouting data</div>
                </div>
            </>
        );
    }

    if (!scoutingReady) {
        return (
            <><style>{animationStyles}</style><SubNav />
                <main className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
                    <div className="max-w-2xl mx-auto pt-12">
                        <div className="text-center mb-8">
                            <h1 className="text-3xl font-bold text-indigo-800 mb-2">Location Scout AI</h1>
                            <p className="text-gray-600">Let me find the perfect filming locations for your project.</p>
                        </div>
                        {renderSetupChat()}
                    </div>
                </main>
            </>
        );
    }

    return (
        <main id="Location">
            <SEO
              title="Location Scouting"
              description="Scout and manage filming locations for your production with ProducerBuddy's location management tools."
              url="https://producerbuddy.pilomation.com/location"
              robots="noindex, nofollow"
            />
            <style>{animationStyles}</style>
            <SubNav />

            <div className="location-detection-container">
                <AutoLocationDetection onLocationDetected={handleLocationDetected} defaultLocation={mapCenter} useDefaultOnly={false} />
                {userLocationDetected && locationDetails && (
                    <div className="location-notification">

                        <button className="save-location-btn" onClick={saveLocationToBackend}>Save This Location</button>
                    </div>
                )}
            </div>

            <div id="bot" className={isMapMaximized ? 'map-maximized' : ''}>
                <div id="locationMessage" className={isMapMaximized ? 'hidden' : ''}>
                    <div id="locationMessages">
                        {chatMessages.map((msg, idx) => (
                            <div key={idx} className={`message-wrapper ${msg.sender}`}>
                                <div className={`message-bubble ${msg.sender === 'user' ? 'locationUserMessage' : 'locationAIMessage'}`}>
                                    {msg.text?.isHtml ? (
                                        <div dangerouslySetInnerHTML={{ __html: msg.text.content }} />
                                    ) : (
                                        <div
                                            className="message-content"
                                            dangerouslySetInnerHTML={{ __html: parseMarkdown(msg.text) }}
                                        />
                                    )}
                                </div>
                            </div>
                        ))}
                        <div ref={chatEndRef} />
                    </div>
                    <div id="locationMessageInput">
                        <textarea name="locationAILanding" id="budgetAILanding" placeholder="Ask about locations..." />
                        <div id="locationMessageButtons">
                            <MdFileUpload className="upload" />
                            <IoMdSend className="sendButton" onClick={handleAIResponse} />
                        </div>
                    </div>
                </div>
                <div id="map" ref={mapContainerRef} className={isMapMaximized ? 'maximized' : ''} style={{ height: isMapMaximized ? '600px' : '400px', width: '100%' }}>
                    <div className="map-controls">
                        {isMapMaximized ? <IoMdContract className="map-size-toggle" onClick={toggleMapSize} title="Minimize map" /> : <IoMdExpand className="map-size-toggle" onClick={toggleMapSize} title="Maximize map" />}
                    </div>
                    {isClient && (
                        <Suspense fallback={<div className="loading-map">Loading map...</div>}>
                            <MapComponent center={mapCenter} selectedLocation={selectedLocation}>
                                <LocationList onLocationClick={handleLocationClick} aiLocations={aiLocations} />
                            </MapComponent>
                        </Suspense>
                    )}
                </div>
                {!isMapMaximized && <LocationList onLocationClick={handleLocationClick} aiLocations={aiLocations} />}
            </div>

            <div id="title"><h1>Location</h1><IoMdAdd id="add" onClick={toggleAddEntry} /></div>
            <div id="locationTable" className={isMapMaximized ? 'hidden' : ''}>
                <div id="columnTitles" className="entry">
                    <h2 className="name">Name</h2><h2 className="address">Address</h2><h2 className="owner">Owner</h2>
                    <h2 className="startDate">Start Availability</h2><h2 className="endDate">End Availability</h2>
                    <h2 className="Contact">Contact</h2><h2 className="form">Form</h2><h2>Delete</h2>
                </div>
                {showAddEntry && (
                    <div id="addEntry" className="entry">
                        <input type="text" placeholder="Name" name="name" onChange={handleInputChange} />
                        <input type="text" placeholder="Address" name="address" onChange={handleInputChange} />
                        <input type="text" placeholder="Owner" name="owner" onChange={handleInputChange} />
                        <input type="date" name="startDate" id="startDate" onChange={handleInputChange} />
                        <input type="date" name="endDate" id="endDate" onChange={handleInputChange} />
                        <input type="email" placeholder="Enter your email" name="contact" onChange={handleInputChange} />
                        <input type="file" placeholder="Location Document" name="form" id="file" onChange={handleFileChange} />
                        <button onClick={addLocation}>Add</button>
                    </div>
                )}
                {locations.map((location) => (
                    <div key={location.location_id} className="entry">
                        <p>{location.location_title}</p><p>{location.address_line}</p><p>{location.user}</p>
                        <p>{location.start_availability}</p><p>{location.end_availability}</p>
                        <p>{location.contact}</p><p>{location.form}</p>
                        <button onClick={() => deleteLocation(location.location_id)}>Delete</button>
                    </div>
                ))}
            </div>
        </main>
    );
};
